import { WORK_CATEGORIES } from "../constants";
import {
  WorkCategoryCounts,
  WorkItem,
  WorkKind,
  WorkOrder,
  WorkQueueView,
  WorkStatusFilter,
} from "../types";

const rankOf = (kind: WorkKind) =>
  WORK_CATEGORIES.find((category) => category.kind === kind)?.rank ??
  WORK_CATEGORIES.length;

const byOldest = (a: WorkItem, b: WorkItem) => {
  if (a.createdAt === null || b.createdAt === null) {
    return Number(a.createdAt === null) - Number(b.createdAt === null);
  }
  return Date.parse(a.createdAt) - Date.parse(b.createdAt);
};

const COMPARATORS: Record<WorkOrder, (a: WorkItem, b: WorkItem) => number> = {
  priority: (a, b) =>
    rankOf(a.kind) - rankOf(b.kind) ||
    b.priorityWeight - a.priorityWeight ||
    byOldest(a, b),
  oldest: byOldest,
};

const matchesStatus = (item: WorkItem, status: WorkStatusFilter) =>
  status === "all" || item.status === status;

const matchesQuery = (item: WorkItem, query: string) =>
  `${item.symbol} ${item.subtitle}`
    .toLowerCase()
    .includes(query.trim().toLowerCase());

export const filterWorkItems = (
  items: WorkItem[],
  { category, status, query, order }: WorkQueueView,
): WorkItem[] =>
  items
    .filter(
      (item) =>
        (category === "all" || item.kind === category) &&
        matchesStatus(item, status) &&
        matchesQuery(item, query),
    )
    .sort(COMPARATORS[order]);

export const countByCategory = (
  items: WorkItem[],
  status: WorkStatusFilter,
): WorkCategoryCounts => {
  const inStatus = items.filter((item) => matchesStatus(item, status));
  const counts = { all: inStatus.length } as WorkCategoryCounts;
  WORK_CATEGORIES.forEach(({ kind: category }) => {
    counts[category] = inStatus.filter(({ kind }) => kind === category).length;
  });
  return counts;
};

export const emptyCopyOf = (
  { status, query }: WorkQueueView,
  hasUnloadedCategory: boolean,
) => {
  if (query.trim() !== "") {
    return {
      title: "No matching work",
      description: "Try another search or widen the filters.",
    };
  }
  if (status === "pending" && !hasUnloadedCategory) {
    return {
      title: "All caught up in this view",
      description: "New work will appear here.",
    };
  }
  return {
    title: "No matching work",
    description: "Choose another category or status.",
  };
};
